// The chart text steps and the screen band (R1796, ui-map-interface.md
// §17.4; mockup round 086 cards 01 and 02).
//
// A canvas piece never names a size. Each word role of a piece names one of
// four steps in its `text` table of `chart.toml` — note, label, name or
// feature — and the face of the reading sets the size of the step.
//
// A world-placed word keeps its world place, but its SCREEN size stays inside
// the band `band_min` to `band_max` times its step: between the two it grows
// with the zoom as the water does, and outside them it stands still on the
// screen. Under its zoom gate the step hides. The quad shader applies both
// as ONE scale per step per frame (`uStep`), so a static pack of words never
// repacks for the camera (§10).
//
// The module is pure: no engine, no DOM. `steps.test.ts` pins it.

import { CHART, CHART_STEPS, type ChartStep } from '../../gen/presentation';
import type { ChartFace } from '../gfx/atlas';
import { F_STEP_SHIFT } from '../layer';

export { CHART_STEPS, type ChartStep };

/** The index of each step in `CHART_STEPS`, so a glyph pays no search. */
const STEP_INDEX: Readonly<Record<ChartStep, number>> = {
  note: CHART_STEPS.indexOf('note'),
  label: CHART_STEPS.indexOf('label'),
  name: CHART_STEPS.indexOf('name'),
  feature: CHART_STEPS.indexOf('feature'),
};

/** The size a step is set at in one face, in px at zoom 1. */
export function stepPx(face: ChartFace, step: ChartStep): number {
  const s = CHART.text.steps[step];
  return face === 'fell' ? s.fellPx : s.monoPx;
}

/** Whether a step stands at a zoom: false under its zoom gate. */
export function stepOpen(step: ChartStep, zoom: number): boolean {
  return zoom >= CHART.text.steps[step].zoomGate;
}

/**
 * The world scale of a step at a zoom: what the shader multiplies a glyph's
 * box and its offset from the pivot by. It is `clamp(zoom, min, max) / zoom`,
 * so the screen size `stepPx × zoom × scale` stays inside the band, and 0
 * under the zoom gate, which collapses the quad to its pivot.
 */
export function bandScale(step: ChartStep, zoom: number): number {
  if (!(zoom > 0) || !stepOpen(step, zoom)) return 0;
  const t = CHART.text;
  return Math.min(t.bandMax, Math.max(t.bandMin, zoom)) / zoom;
}

/**
 * The four scales of one frame, written into `out` in step order. The chart
 * controller calls it once a frame and the two chart layers hand the four
 * numbers to their quad batches as the `uStep` uniform.
 */
export function bandScalesInto(zoom: number, out: Float32Array): Float32Array {
  for (let i = 0; i < CHART_STEPS.length; i++) out[i] = bandScale(CHART_STEPS[i], zoom);
  return out;
}

/** The flag bits a glyph quad of a step carries: the step index plus one. */
export function stepFlags(step: ChartStep): number {
  return (STEP_INDEX[step] + 1) << F_STEP_SHIFT;
}
